import React, { useEffect, useRef, useState } from 'react';
import './Community.css';
import { useLanguage } from '../context/LanguageContext';

const featureIcons = [
  <svg viewBox="0 0 40 40" fill="none">
    <rect x="6" y="10" width="28" height="20" stroke="currentColor" strokeWidth="1.5" rx="2"/>
    <path d="M6 16 L20 24 L34 16" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="20" y1="24" x2="20" y2="30" stroke="currentColor" strokeWidth="1.5"/>
  </svg>,
  <svg viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="12" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="20" cy="20" r="6" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 8 L20 4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 36 L20 32" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 20 L4 20" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M36 20 L32 20" stroke="currentColor" strokeWidth="1.5"/>
  </svg>,
  <svg viewBox="0 0 40 40" fill="none">
    <path d="M20 6 L24 14 L34 16 L27 24 L28 34 L20 29 L12 34 L13 24 L6 16 L16 14 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
];

const colorEditionsData = [
  { color: "#1a1a1a", border: "#333", image: "/PACKAGING-RED.png" },
  { color: "#f0f0f0", border: "#ccc", image: "/PACKAGING-WHITE.png" },
  { color: "#8b0000", border: "#a00", image: "/PACKAGING-BLACK.png" }
];

const Community = () => {
  const [isInView, setIsInView] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedColor, setSelectedColor] = useState(0);
  const sectionRef = useRef(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(language === 'en' 
      ? `Welcome to the Inner Circle! We'll contact you at: ${email}`
      : `¡Bienvenido al Círculo Interno! Te contactaremos a: ${email}`
    );
    setEmail('');
  };

  return (
    <section className="community" id="community" ref={sectionRef}>
      <div className="community-background">
        <div className="fire-gradient"></div>
      </div>

      <div className={`community-content ${isInView ? 'visible' : ''}`}>
        <div className="community-header">
          <span className="section-label">{t.community.sectionLabel}</span>
          <h2 className="section-title">
            <span>{t.community.sectionTitle1}</span>
            <span className="accent">{t.community.sectionTitle2}</span>
          </h2>
          <p className="section-description">
            {t.community.sectionDescription}
          </p>
        </div>

        <div className="packaging-showcase">
          <div className="packaging-image">
            <img 
              src={colorEditionsData[selectedColor].image} 
              alt="Volcano Vodka Packaging" 
              key={selectedColor}
            />
            <div className="packaging-glow"></div>
          </div>
          <div className="packaging-details">
            <h3 className="packaging-title">{t.community.packagingTitle}</h3>
            <p className="packaging-description">
              {t.community.packagingDescription}
            </p>
            <div className="color-editions">
              <span className="editions-label">{t.community.availableIn}</span>
              <div className="edition-swatches">
                {t.community.colors.map((edition, index) => (
                  <div 
                    key={index} 
                    className={`edition-swatch ${selectedColor === index ? 'active' : ''}`}
                    onClick={() => setSelectedColor(index)}
                  >
                    <span 
                      className="swatch-color" 
                      style={{ background: colorEditionsData[index].color, borderColor: colorEditionsData[index].border }}
                    ></span>
                    <span className="swatch-name">{edition.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="benefits-grid">
          {t.community.features.map((feature, index) => (
            <div 
              key={index} 
              className="benefit-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="benefit-icon">{featureIcons[index]}</div>
              <h3 className="benefit-title">{feature.title}</h3>
              <p className="benefit-description">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="join-section">
          <div className="join-content">
            <h3 className="join-title">{t.community.joinTitle}</h3>
            <p className="join-description">
              {t.community.joinDescription}
            </p>
            <form className="join-form" onSubmit={handleSubmit}>
              <div className="input-wrapper">
                <input
                  type="email"
                  placeholder={t.community.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className="join-btn">
                  <span>{t.community.joinButton}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
            </form>
            <p className="join-disclaimer">
              {t.community.disclaimer}
            </p>
          </div>
        </div>

        <div className="community-features">
          <div className="feature-row">
            {t.community.featureItems.map((item, index) => (
              <div key={index} className="feature-item">
                <div className="feature-number">{item.number}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Community;
